import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { walletKeys } from '@/constant/queryKeys';
import {
    getWalletListOptions,
    getPrimaryWalletOptions,
    getWalletBalanceOptions,
    getAllWalletBalancesOptions,
    getTokenStatsOptions,
    postConnectWalletOptions,
    deleteWalletOptions,
    postPrimaryWalletOptions,
    Wallet
} from '@/services/queryOptions/walletQueryOptions';
import { useAuthStore } from './useAuthStore';

// Individual query hooks for specific use cases
export function useWalletList() {
    const { sessionValidated, isAuthenticated } = useAuthStore();
    return useQuery(getWalletListOptions(sessionValidated, isAuthenticated));
}

export function usePrimaryWallet() {
    const { sessionValidated, isAuthenticated } = useAuthStore();
    return useQuery(getPrimaryWalletOptions(sessionValidated, isAuthenticated));
}

export function useWalletBalance(publicKey?: string, walletExists?: boolean) {
    return useQuery(getWalletBalanceOptions(publicKey, walletExists));
}

export function useAllWalletBalances() {
    const { sessionValidated, isAuthenticated } = useAuthStore();
    return useQuery(getAllWalletBalancesOptions(sessionValidated, isAuthenticated));
}

export function useTokenStats(tokenName: string) {
    return useQuery(getTokenStatsOptions(tokenName));
}

export function usePostPrimaryWallet() {
    const walletListQuery = useWalletList();
    return useMutation(postPrimaryWalletOptions(walletListQuery.data));
}

export function useConnectWallet() {
    const queryClient = useQueryClient();
    const connectWalletMutation = useMutation({
        ...postConnectWalletOptions(),
        onSuccess: () => {
            // Invalidate all wallet-related queries
            queryClient.invalidateQueries({ queryKey: walletKeys.getWalletList });
            queryClient.invalidateQueries({ queryKey: walletKeys.getPrimaryWallet });
            queryClient.invalidateQueries({ queryKey: walletKeys.getAllWalletBalances });
        },
    });

    return connectWalletMutation;
}

export function useDeleteWallet() {
    const queryClient = useQueryClient();

    return useMutation({
        ...deleteWalletOptions(),
        onMutate: async (walletId: number) => {
            // Cancel ongoing refetches
            await queryClient.cancelQueries({ queryKey: walletKeys.getWalletList });

            // Snapshot current data
            const previousWalletList = queryClient.getQueryData(walletKeys.getWalletList);

            // Optimistically update wallet list
            queryClient.setQueryData(walletKeys.getWalletList, (old: any) => {
                if (!old?.wallets) return old;
                return {
                    ...old,
                    wallets: old.wallets.filter((w: Wallet) => w.id !== walletId),
                };
            });

            return { previousWalletList };
        },
        onError: (err, variables, context) => {
            // Rollback on error
            if (context?.previousWalletList) {
                queryClient.setQueryData(walletKeys.getWalletList, context.previousWalletList);
            }
        },
        onSuccess: (data) => {
            // Update cache with server response (includes balances)
            queryClient.setQueryData(walletKeys.getWalletList, { wallets: data.wallets });

            // Invalidate other wallet-related queries
            queryClient.invalidateQueries({ queryKey: walletKeys.getPrimaryWallet });
            queryClient.invalidateQueries({ queryKey: walletKeys.getAllWalletBalances });
        },
    });
}
